import * as React from 'react';
import { forwardRef, ReactNode, useImperativeHandle } from 'react';
import {
   FormControl,
   FormLabel,
   Input,
   Button,
   Flex,
   Link,
   Text,
   Divider,
   Checkbox,
} from '@chakra-ui/react';
import { faLink, faPencil, faTrash } from '@fortawesome/pro-regular-svg-icons';
import { TiptapButton } from '../../components/TiptapButton';
import type { Editor } from '@tiptap/core';
import { shadow } from '@dozuki/web-js/primitives';
import { useLocalization } from '~/framework';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

declare type LinkEditorState = 'preview' | 'edit' | null;
export type { LinkEditorState };

type LinkEditorProps = { children?: ReactNode; editor: Editor };

type LinkEditorInnerProps = {
   editor: Editor;
   url: string;
   text: string;
   newWindow: boolean;
   setUrl: (url: string) => void;
   setText: (text: string) => void;
   setNewWindow: (newWindow: boolean) => void;
   handleEdit: () => void;
   handleSave: () => void;
   handleRemove: () => void;
   handleCancel: () => void;
};

const linkEditorWidth = {
   base: '350px',
   sm: '350px',
   md: '500px',
   lg: '600px',
};

const linkEditorStyle = {
   bgColor: 'gray.50',
   border: '1px',
   borderColor: 'gray.300',
   borderRadius: 'md',
   padding: '2.5',
   maxW: '90vw',
   boxShadow: shadow[3],
};

const LinkPreview = (props: LinkEditorInnerProps) => {
   const { _js } = useLocalization();
   const buttonStyle = {
      marginLeft: 2,
      size: 'md',
   };

   if (!props.editor.isActive('link')) {
      return null;
   }

   return (
      <Flex maxWidth={linkEditorWidth} {...linkEditorStyle}>
         <Button
            as={Link}
            href={props.url}
            leftIcon={<FontAwesomeIcon icon={faLink} color="var(--chakra-colors-gray-500)" />}
            flexGrow={2}
            variant="link"
            isExternal={true}
         >
            <Text
               fontSize="md"
               alignSelf="center"
               color="blue.500"
               noOfLines={1}
               wordBreak="break-all"
            >
               {props.url}
            </Text>
         </Button>
         <TiptapButton
            icon={<FontAwesomeIcon icon={faPencil} />}
            onClick={props.handleEdit}
            title={_js('Edit link')}
            iconButtonStyle={buttonStyle}
         />
         <TiptapButton
            icon={<FontAwesomeIcon icon={faTrash} />}
            onClick={props.handleRemove}
            title={_js('Remove link')}
            iconButtonStyle={buttonStyle}
         />
      </Flex>
   );
};

const LinkEdit = (props: LinkEditorInnerProps) => {
   const { _js } = useLocalization();
   const { url, setUrl, text, setText, handleSave, handleCancel, setNewWindow, newWindow } = props;
   const textRef = React.useRef<HTMLInputElement>(null);
   const urlRef = React.useRef<HTMLInputElement>(null);

   React.useEffect(() => {
      const focusText = () => textRef.current?.focus();
      const focusUrl = () => urlRef.current?.focus();
      // If we're editing an existing link, focus the link text
      const focusCallback = url ? focusText : focusUrl;
      setTimeout(focusCallback, 50);
   }, []);

   const buttonStyle = {
      margin: '2',
      marginTop: '4',
      marginRight: '0',
   };

   const handleKeyDown = (event: React.KeyboardEvent) => {
      if (event.key !== 'Enter') {
         return;
      }
      event.preventDefault();
      if (url) {
         handleSave();
      }
   };

   const handleChangeUrl = (event: React.ChangeEvent<HTMLInputElement>) => {
      setUrl(event.target.value);
   };

   const handleChangeText = (event: React.ChangeEvent<HTMLInputElement>) => {
      setText(event.target.value);
   };

   const handleToggleNewWindow = (event: React.ChangeEvent<HTMLInputElement>) => {
      setNewWindow(event.target.checked);
   };

   return (
      <FormControl width={linkEditorWidth} {...linkEditorStyle}>
         <FormLabel>{_js('Link text')}</FormLabel>
         <Input
            bgColor="white"
            fontSize="sm"
            value={text}
            onChange={handleChangeText}
            onKeyDown={handleKeyDown}
            ref={textRef}
         />
         <Divider orientation="horizontal" borderStyle="none" />
         <FormLabel>{_js('Link address')}</FormLabel>
         <Input
            bgColor="white"
            fontSize="sm"
            placeholder="https://www.example.com"
            value={url}
            onChange={handleChangeUrl}
            onKeyDown={handleKeyDown}
            ref={urlRef}
            type="url"
         />
         <Divider orientation="horizontal" borderStyle="none" />
         <Checkbox
            size="md"
            colorScheme="blue"
            onChange={handleToggleNewWindow}
            isChecked={newWindow}
         >
            {_js('Open link in new tab')}
         </Checkbox>
         <Flex justify="flex-end">
            <Button onClick={handleCancel} {...buttonStyle}>
               {_js('Cancel')}
            </Button>
            <Button
               variant="solid"
               colorScheme="blue"
               onClick={handleSave}
               isDisabled={!url}
               {...buttonStyle}
            >
               {_js('Confirm Link')}
            </Button>
         </Flex>
      </FormControl>
   );
};

export const LinkEditor = forwardRef(function LinkEditor(props: LinkEditorProps, ref) {
   const { editor } = props;
   const [url, setUrl] = React.useState('');
   const [text, setText] = React.useState('');
   const [newWindow, setNewWindow] = React.useState(false);
   const [state, setState] = React.useState('preview');

   const updateSelection = () => {
      editor.commands.extendMarkRange('link');

      if (editor.state.selection.empty) {
         setText('');
         return;
      }

      const { from, to } = editor.state.selection;
      const selectedText = editor.state.doc.textBetween(from, to);
      setText(selectedText);
   };

   useImperativeHandle(ref, () => ({
      show: (state?: LinkEditorState) => {
         if (state) {
            setState(state);
         }
         if (state === 'edit') {
            updateSelection();
         }

         const href = editor.getAttributes('link').href || '';
         setUrl(href);
         const target = editor.getAttributes('link').target || null;
         setNewWindow(target === '_blank');
         return true;
      },
      hide: () => {
         setText('');
         setUrl('');
         setNewWindow(false);
         setState('preview');
         return true;
      },
   }));

   const handleSave = () => {
      let href = url;

      if (href && !hasValidProtocol(href)) {
         href = 'https://' + href;
         setUrl(href);
      }

      const replacementText = text || href;
      const target = newWindow === true ? '_blank' : null;
      editor.commands.insertLink(replacementText, href, target);

      setState('preview');
   };

   const handleRemove = () => {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      setUrl('');
   };

   const handleCancel = () => {
      const href = editor.getAttributes('link').href;
      if (href) {
         setUrl(href);
      }
      setState('preview');
      editor.commands.focus();
   };

   const handleEdit = () => {
      updateSelection();
      setState('edit');
   };

   const innerProps = {
      editor,
      url,
      text,
      newWindow,
      setUrl,
      setText,
      setNewWindow,
      handleEdit,
      handleSave,
      handleRemove,
      handleCancel,
   };

   return (
      editor &&
      (state == 'preview' ? <LinkPreview {...innerProps} /> : <LinkEdit {...innerProps} />)
   );
});

/**
 * Check if the provided url has a valid protocol.
 *
 * Examples:
 *
 *       www.dozuki.com => false
 *       https://www.dozuki.com => true
 *       mailto:dozuki@dozuki.com => true
 *
 * Uses the default javascript url validator.
 */
function hasValidProtocol(url: string): boolean {
   let parsedURL: URL | null;

   try {
      parsedURL = new URL(url);
   } catch (_) {
      parsedURL = null;
   }

   return Boolean(parsedURL?.protocol);
}
